import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { Box } from '@chakra-ui/react';
import { useMentions } from '../../api/useMentions';
import { type MentionSuggestion } from '../../api/mentions';
import { MentionSuggestionsPopup } from './MentionSuggestionsPopup';
import {
  MENTION_CHIP_CLASS,
  parseServerContent,
  plainTextFromDom,
  serializeDom,
  type MentionMeta,
} from './mentionUtils';

export interface MentionEditorHandle {
  /** Server-format string with `<tag>` markers. */
  getSerialized: () => string;
  /** Human-readable plain text (mentions become `@name`). */
  getPlainText: () => string;
  /** Wipes the editor content back to empty. */
  clear: () => void;
  /** Focus the editable area (caret to end). */
  focus: () => void;
  /** Replace the content with plain text (caret to end) — used by the
   *  composer's slash-command autocomplete. */
  setText: (text: string) => void;
}

export interface MentionEditorProps {
  placeholder?: string;
  multiline?: boolean;
  autoFocus?: boolean;
  /** Server-format string used to seed the editor (chips are restored). */
  initialValue?: string;
  /** Fires with the latest plain-text view on every input. */
  onChange?: (_plainText: string) => void;
  /** Forwarded keydown — Composer uses this to handle Enter / Shift+Enter. */
  onKeyDown?: (_event: KeyboardEvent<HTMLDivElement>) => void;
  /** Forwarded keyup. */
  onKeyUp?: (_event: KeyboardEvent<HTMLDivElement>) => void;
  className?: string;
  /** Minimum visual height in pixels. Multiline mode honors this. */
  minHeight?: number;
  /** Maximum height before vertical scroll engages. */
  maxHeight?: number;
  /** Test hook attached to the editable root. */
  testId?: string;
}

const QUERY_REGEX = /(?:^|\s)@(\S*)$/;
const SUGGEST_DEBOUNCE_MS = 350;
// The in-progress `@query` is wrapped in this span so it renders in the
// mention color while typing. It carries no data — serialization walks into
// it and emits its plain text — and is unwrapped on dismiss or replaced by a
// chip on selection.
const MENTION_DRAFT_CLASS = 'agentforge-mention-draft';
const DRAFT_REGEX = /^@(\S*)$/;

const isSelfOrAncestorChip = (node: Node | null): boolean => {
  let cur: Node | null = node;
  while (cur) {
    if (
      cur.nodeType === Node.ELEMENT_NODE &&
      (cur as HTMLElement).classList?.contains(MENTION_CHIP_CLASS)
    ) {
      return true;
    }
    cur = cur.parentNode;
  }
  return false;
};

const buildChipNode = (meta: MentionMeta): HTMLSpanElement => {
  const span = document.createElement('span');
  span.className = MENTION_CHIP_CLASS;
  span.setAttribute('contenteditable', 'false');
  span.dataset.mentionId = meta.id;
  span.dataset.mentionEntity = meta.entity;
  span.dataset.mentionName = meta.name;
  span.textContent = `@${meta.name}`;
  return span;
};

// Populate a contenteditable root from server-format content. Newlines in
// text segments become `<br>` so the editor lays them out.
const hydrateRoot = (root: HTMLElement, initial: string): void => {
  root.innerHTML = '';
  const tokens = parseServerContent(initial);
  tokens.forEach((token) => {
    if (token.type === 'mention') {
      root.appendChild(buildChipNode(token.mention));
      return;
    }
    const lines = token.text.split('\n');
    lines.forEach((line, i) => {
      if (i > 0) root.appendChild(document.createElement('br'));
      if (line.length > 0) root.appendChild(document.createTextNode(line));
    });
  });
};

const moveCaretAfter = (node: Node): void => {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

const focusEnd = (el: HTMLElement): void => {
  el.focus();
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const MentionEditor = forwardRef<
  MentionEditorHandle,
  MentionEditorProps
>(
  (
    {
      placeholder,
      multiline = false,
      autoFocus = false,
      initialValue,
      onChange,
      onKeyDown,
      onKeyUp,
      className,
      minHeight,
      maxHeight,
      testId,
    },
    ref
  ) => {
    const rootRef = useRef<HTMLDivElement>(null);
    // The live draft span wrapping the in-progress `@query`, if any.
    const draftRef = useRef<HTMLSpanElement | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // `query` drives popup visibility and the "No matches for …" label.
    // `debouncedQuery` is the queryKey passed to TanStack — lags by
    // SUGGEST_DEBOUNCE_MS so we don't fire on every keystroke.
    const [query, setQuery] = useState<string | null>(null);
    const [debouncedQuery, setDebouncedQuery] = useState<string | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [popupPosition, setPopupPosition] = useState<{
      bottom: number;
      left: number;
    } | null>(null);
    const [isEmpty, setIsEmpty] = useState(true);

    const { data: suggestions = [], isFetching: loading } =
      useMentions(debouncedQuery);

    // Debounce query → debouncedQuery. When query closes (null) we clear
    // immediately so stale results don't flash on re-open.
    useEffect(() => {
      if (query === null) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setDebouncedQuery(null);
        return;
      }
      debounceRef.current = setTimeout(
        () => setDebouncedQuery(query),
        SUGGEST_DEBOUNCE_MS
      );
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, [query]);

    // Reset highlight to 0 whenever fresh suggestions arrive.
    useEffect(() => {
      setHighlightedIndex(0);
    }, [suggestions]);

    // Recompute the "is the editor visually empty" state so the CSS
    // placeholder pseudo-element can be hidden when the user has typed.
    const refreshEmpty = useCallback(() => {
      const el = rootRef.current;
      if (!el) return;
      const text = plainTextFromDom(el);
      setIsEmpty(text.length === 0);
    }, []);

    // Replace the draft span with its plain text so a dismissed mention stops
    // rendering in the mention color. No-op when there's no active draft.
    const unwrapDraft = useCallback(() => {
      const span = draftRef.current;
      draftRef.current = null;
      const parent = span?.parentNode;
      if (!span || !parent) return;
      parent.replaceChild(
        document.createTextNode(span.textContent ?? ''),
        span
      );
      parent.normalize();
    }, []);

    const closePopup = useCallback(() => {
      unwrapDraft();
      setQuery(null);
      setPopupPosition(null);
    }, [unwrapDraft]);

    useImperativeHandle(
      ref,
      (): MentionEditorHandle => ({
        getSerialized: () =>
          rootRef.current ? serializeDom(rootRef.current) : '',
        getPlainText: () =>
          rootRef.current ? plainTextFromDom(rootRef.current) : '',
        clear: () => {
          if (rootRef.current) {
            rootRef.current.innerHTML = '';
            setIsEmpty(true);
            onChange?.('');
          }
          closePopup();
        },
        focus: () => {
          if (rootRef.current) focusEnd(rootRef.current);
        },
        setText: (text: string) => {
          const el = rootRef.current;
          if (!el) return;
          el.textContent = text;
          setIsEmpty(text.length === 0);
          onChange?.(text);
          focusEnd(el);
          closePopup();
        },
      }),
      [closePopup, onChange]
    );

    // One-time hydration from initialValue. Subsequent prop changes are
    // ignored — once the user has begun editing, the DOM is the source of
    // truth and we don't want to clobber it from above.
    useEffect(() => {
      const el = rootRef.current;
      if (!el) return;
      if (initialValue && initialValue.length > 0) {
        hydrateRoot(el, initialValue);
      }
      refreshEmpty();
      if (autoFocus) focusEnd(el);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Position the popup above the input. It opens upward, with its bottom
    // edge clearing the whole input container (not just the `@` glyph) so the
    // list never overlaps the field; horizontally it aligns to the `@`.
    const positionAbove = useCallback((el: HTMLElement) => {
      const container = el.closest(
        '[data-mention-input-container]'
      ) as HTMLElement | null;
      const anchorTop = (container ?? el).getBoundingClientRect().top;
      setPopupPosition({
        bottom: window.innerHeight - anchorTop + 8,
        left: el.getBoundingClientRect().left,
      });
    }, []);

    // Caret to the end of a node's contents.
    const caretToEndOf = (node: Node): void => {
      const sel = window.getSelection();
      if (!sel) return;
      const r = document.createRange();
      r.selectNodeContents(node);
      r.collapse(false);
      sel.removeAllRanges();
      sel.addRange(r);
    };

    // Inspect the caret and decide whether a mention trigger is active. While
    // active the `@query` lives in a draft span (so it renders in the mention
    // color); this keeps that span in sync, growing/closing it as the user
    // types, and drives the popup query + position.
    const detectTrigger = useCallback(() => {
      const sel = window.getSelection();
      const root = rootRef.current;
      if (!sel || !root || sel.rangeCount === 0) {
        if (query !== null) closePopup();
        return;
      }
      const range = sel.getRangeAt(0);
      const caretNode = range.startContainer;
      if (!range.collapsed || !root.contains(caretNode)) {
        if (query !== null) closePopup();
        return;
      }

      const draft = draftRef.current;

      // Caret sits inside the active draft span — keep it open while the text
      // still looks like `@word`, otherwise dismiss.
      if (draft && draft.contains(caretNode)) {
        const m = (draft.textContent ?? '').match(DRAFT_REGEX);
        if (m) {
          positionAbove(draft);
          setQuery(m[1]);
          return;
        }
        closePopup();
        return;
      }

      // A character can land in the text node right after the draft span when
      // typing at its boundary — pull that leading run back into the span so
      // the mention keeps growing colored.
      if (
        draft &&
        caretNode.nodeType === Node.TEXT_NODE &&
        caretNode.previousSibling === draft
      ) {
        const tn = caretNode as Text;
        const txt = tn.textContent ?? '';
        const wsIdx = txt.search(/\s/);
        const take = wsIdx === -1 ? txt : txt.slice(0, wsIdx);
        if (take) {
          draft.textContent = (draft.textContent ?? '') + take;
          tn.textContent = txt.slice(take.length);
          caretToEndOf(draft);
        }
        const m = (draft.textContent ?? '').match(DRAFT_REGEX);
        if (m) {
          positionAbove(draft);
          setQuery(m[1]);
          return;
        }
        closePopup();
        return;
      }

      // Fresh trigger in a plain text node — wrap `@query` into a draft span.
      if (
        caretNode.nodeType !== Node.TEXT_NODE ||
        isSelfOrAncestorChip(caretNode)
      ) {
        if (query !== null) closePopup();
        return;
      }
      const textNode = caretNode as Text;
      const offset = range.startOffset;
      const before = (textNode.textContent ?? '').slice(0, offset);
      const match = before.match(QUERY_REGEX);
      if (!match) {
        if (query !== null) closePopup();
        return;
      }
      const q = match[1];
      const atOffset = offset - q.length - 1;
      const wrapRange = document.createRange();
      wrapRange.setStart(textNode, atOffset);
      wrapRange.setEnd(textNode, offset);
      const span = document.createElement('span');
      span.className = MENTION_DRAFT_CLASS;
      try {
        wrapRange.surroundContents(span);
      } catch {
        if (query !== null) closePopup();
        return;
      }
      draftRef.current = span;
      caretToEndOf(span);
      positionAbove(span);
      setQuery(q);
    }, [closePopup, positionAbove, query]);

    // Insert the chosen suggestion as an atomic chip in place of the draft
    // span. Caret lands after a trailing space.
    const insertMention = useCallback(
      (suggestion: MentionSuggestion) => {
        const root = rootRef.current;
        const span = draftRef.current;
        if (!root || !span || !span.parentNode) return;

        const chip = buildChipNode({
          id: suggestion.id,
          entity: suggestion.entity,
          name: suggestion.name,
        });
        span.parentNode.replaceChild(chip, span);
        draftRef.current = null;

        // Trailing space lets the user keep typing without immediately
        // re-triggering the popup.
        const spacer = document.createTextNode(' ');
        chip.after(spacer);

        moveCaretAfter(spacer);
        closePopup();
        refreshEmpty();
        onChange?.(plainTextFromDom(root));
      },
      [closePopup, onChange, refreshEmpty]
    );

    const handleInput = useCallback(() => {
      const root = rootRef.current;
      if (!root) return;
      onChange?.(plainTextFromDom(root));
      refreshEmpty();
      detectTrigger();
    }, [detectTrigger, onChange, refreshEmpty]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        // Popup steals navigation / commit keys.
        if (query !== null && suggestions.length > 0) {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((i) => (i + 1) % suggestions.length);
            return;
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex(
              (i) => (i - 1 + suggestions.length) % suggestions.length
            );
            return;
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            const picked = suggestions[highlightedIndex];
            if (picked) insertMention(picked);
            return;
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            closePopup();
            return;
          }
        }
        // Empty popup state — Escape still closes so the user can dismiss.
        if (query !== null && event.key === 'Escape') {
          event.preventDefault();
          closePopup();
          return;
        }

        onKeyDown?.(event);
        if (event.defaultPrevented) return;

        // Single-line mode never inserts a literal newline.
        if (!multiline && event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          return;
        }
        // Multiline mode: native Enter on contenteditable defaults to a
        // new <div> wrapper which complicates serialization. Force a
        // simple <br> instead.
        if (multiline && event.key === 'Enter' && event.shiftKey) {
          event.preventDefault();
          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;
          const range = sel.getRangeAt(0);
          range.deleteContents();
          const br = document.createElement('br');
          range.insertNode(br);
          // Zero-width text node so the caret has somewhere to land after
          // the <br> when it's the last child.
          const placeholder = document.createTextNode('​');
          br.after(placeholder);
          moveCaretAfter(placeholder);
          handleInput();
        }
      },
      [
        closePopup,
        handleInput,
        highlightedIndex,
        insertMention,
        multiline,
        onKeyDown,
        query,
        suggestions,
      ]
    );

    const handleKeyUp = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        onKeyUp?.(event);
        // Arrow keys / mouse clicks move the caret without firing input,
        // so refresh trigger detection here too.
        if (
          event.key === 'ArrowLeft' ||
          event.key === 'ArrowRight' ||
          event.key === 'Home' ||
          event.key === 'End'
        ) {
          detectTrigger();
        }
      },
      [detectTrigger, onKeyUp]
    );

    const handlePaste = useCallback(
      (event: ClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain') ?? '';
        const cleaned = multiline ? text : text.replace(/\r?\n/g, ' ');
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const frag = document.createDocumentFragment();
        const lines = cleaned.split('\n');
        lines.forEach((line, i) => {
          if (i > 0) frag.appendChild(document.createElement('br'));
          if (line) frag.appendChild(document.createTextNode(line));
        });
        const lastNode = frag.lastChild;
        range.insertNode(frag);
        if (lastNode) moveCaretAfter(lastNode);
        handleInput();
      },
      [handleInput, multiline]
    );

    const handleBlur = useCallback(() => {
      // Close on blur but with a small delay so a click on the popup can
      // resolve first (popup's onMouseDown preventDefault handles the
      // ordering, but blur fires anyway on click-outside).
      setTimeout(() => {
        if (document.activeElement === rootRef.current) return;
        closePopup();
      }, 120);
    }, [closePopup]);

    const rootStyle: CSSProperties = {
      minHeight: minHeight ? `${minHeight}px` : undefined,
      maxHeight: maxHeight ? `${maxHeight}px` : undefined,
      overflowY: maxHeight ? 'auto' : undefined,
      outline: 'none',
      whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
      wordBreak: multiline ? 'break-word' : 'normal',
      overflowX: multiline ? 'hidden' : 'auto',
    };

    return (
      <Box position="relative" width="100%">
        <Box
          ref={rootRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline={multiline}
          aria-label={placeholder}
          data-placeholder={placeholder}
          data-multiline={multiline}
          data-test-id={testId ?? 'mention-editor'}
          data-empty={isEmpty}
          className={className}
          style={rootStyle}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onPaste={handlePaste}
          onBlur={handleBlur}
          onMouseUp={() => detectTrigger()}
          sx={{
            fontSize: '14px',
            lineHeight: '20px',
            color: 'secondary-900',
            '&[data-empty="true"]::before': {
              content: 'attr(data-placeholder)',
              color: 'secondary-400',
              pointerEvents: 'none',
              display: 'block',
              position: 'absolute',
              top: 0,
              left: '5px',
            },
            [`& .${MENTION_DRAFT_CLASS}`]: {
              color: 'primary-500',
            },
            [`& .${MENTION_CHIP_CLASS}`]: {
              display: 'inline-block',
              padding: '0 6px',
              margin: '0 1px',
              borderRadius: '6px',
              backgroundColor: 'primary-50',
              color: 'primary-700',
              fontWeight: 500,
              userSelect: 'all',
              cursor: 'default',
              whiteSpace: 'nowrap',
              verticalAlign: 'baseline',
              lineHeight: '20px',
            },
          }}
          pl="5px"
        />
        <MentionSuggestionsPopup
          open={query !== null}
          loading={loading}
          query={query ?? ''}
          suggestions={suggestions}
          highlightedIndex={highlightedIndex}
          position={popupPosition}
          onSelect={insertMention}
          onHover={setHighlightedIndex}
        />
      </Box>
    );
  }
);

MentionEditor.displayName = 'MentionEditor';
